/**
 * VariableBindingService
 *
 * Facade for all FunnelVariable binding operations.
 * Single source of truth for bind/unbind/switch logic — replaces
 * scattered mutation code across V2VariableBindingSection handlers.
 *
 * Responsibilities:
 * - Bind variable to screen (boundScreenIds, scope, boundSectionId, screenBindings)
 * - Unbind variable from screen (clean up all per-screen data)
 * - Switch variable (atomic unbind old + bind new)
 * - Create + bind in one call
 * - Create global variable (scope: 'global', no screen binding)
 *
 * Does NOT call Firebase directly — returns mutated FunnelVariable objects.
 * Caller (VariableEditorContext) is responsible for persistence.
 *
 * @module v2-variable-utils
 */

import type { FunnelVariable, VariableOption } from '@segmently/schema-models/v2';
import type { CreateVariableParams } from '../types/variable-utils.types';
import { variableFactory } from './VariableFactory';

// ============================================================================
// Types
// ============================================================================

/** Context for binding a variable to a screen */
export interface BindingContext {
  /** Screen ID to bind to */
  screenId: string;
  /** Optional section ID for FlexibleLayout/WebEmbed multi-section screens */
  sectionId?: string;
}

/** Screen items for auto-populating screenBindings on selection screens */
export interface ScreenItemsContext {
  /** Items from content.options.items */
  items: Array<{ variableValue: string }>;
}

/** Result of a bind/unbind/switch operation — variables to persist */
export interface BindingResult {
  /** Variables that were modified and need to be saved */
  updatedVariables: FunnelVariable[];
}

// ============================================================================
// Helpers
// ============================================================================

/** Ensure every VariableOption has a stable id (backward compat for old data). */
const ensureOptionId = (opt: VariableOption): VariableOption & { id: string } => ({
  ...opt,
  id: opt.id || crypto.randomUUID(),
});

// ============================================================================
// Service
// ============================================================================

/**
 * Extended FunnelVariable with optional fields that the schema exposes
 * but TypeScript strict mode may not see on the base type.
 */
type FunnelVariableFull = FunnelVariable & {
  boundScreenIds?: string[];
  screenBindings?: Record<string, Record<string, string>>;
  screenScoreEffects?: Record<string, unknown[]>;
  boundSectionId?: string;
  scope?: 'screen' | 'global';
};

export class VariableBindingService {

  // ────────────────────────────────────────────────────────────
  // Unbind
  // ────────────────────────────────────────────────────────────

  /**
   * Unbind a variable from a screen.
   * Removes screenId from boundScreenIds, cleans screenBindings,
   * screenScoreEffects, and boundSectionId.
   *
   * @returns Updated variable (caller must persist)
   */
  unbind(variable: FunnelVariable, ctx: BindingContext): FunnelVariable {
    const v = { ...variable } as FunnelVariableFull;

    // Remove screen from boundScreenIds
    v.boundScreenIds = (v.boundScreenIds || []).filter(id => id !== ctx.screenId);

    // Clean per-screen data
    if (v.screenBindings) {
      const { [ctx.screenId]: _, ...rest } = v.screenBindings;
      v.screenBindings = rest;
    }
    if (v.screenScoreEffects) {
      const { [ctx.screenId]: _, ...rest } = v.screenScoreEffects;
      v.screenScoreEffects = rest;
    }

    // Clear section-level binding if it matches
    if (ctx.sectionId && v.boundSectionId === ctx.sectionId) {
      delete v.boundSectionId;
    }

    return v as FunnelVariable;
  }

  // ────────────────────────────────────────────────────────────
  // Bind
  // ────────────────────────────────────────────────────────────

  /**
   * Bind a variable to a screen.
   * Adds screenId to boundScreenIds, sets scope='screen',
   * optionally sets boundSectionId, and auto-populates screenBindings
   * for selection screens when items are provided.
   *
   * @param variable - Variable to bind
   * @param ctx - Screen + optional section context
   * @param screenItems - Optional items for auto-populating screenBindings
   * @returns Updated variable (caller must persist)
   */
  bind(
    variable: FunnelVariable,
    ctx: BindingContext,
    screenItems?: ScreenItemsContext,
  ): FunnelVariable {
    const v = { ...variable } as FunnelVariableFull;

    // Add screen to boundScreenIds (dedup)
    const ids = v.boundScreenIds || [];
    if (!ids.includes(ctx.screenId)) {
      v.boundScreenIds = [...ids, ctx.screenId];
    }

    // Always screen-scoped when binding to a screen
    v.scope = 'screen';

    // Section-level scope for FlexibleLayout/WebEmbed
    if (ctx.sectionId) {
      v.boundSectionId = ctx.sectionId;
    }

    // Auto-populate screenBindings for selection screens
    if (screenItems && screenItems.items.length > 0) {
      let opts = (v.options || []).map(ensureOptionId);
      if (opts.length === 0) {
        // Create options from screen items
        opts = this.createOptionsFromItems(screenItems.items);
        v.options = opts;
      }
      v.screenBindings = {
        ...(v.screenBindings || {}),
        [ctx.screenId]: this.buildPositionalBindings(opts, screenItems.items),
      };
    }

    return v as FunnelVariable;
  }

  // ────────────────────────────────────────────────────────────
  // Switch (unbind old + bind new)
  // ────────────────────────────────────────────────────────────

  /**
   * Switch binding from one variable to another on the same screen.
   * Atomically unbinds the old variable and binds the new one.
   *
   * @param oldVariable - Currently bound variable (will be unbound)
   * @param newVariable - New variable to bind
   * @param ctx - Screen + optional section context
   * @param screenItems - Optional items for auto-populating screenBindings
   * @returns Both updated variables (caller must persist both)
   */
  switch(
    oldVariable: FunnelVariable,
    newVariable: FunnelVariable,
    ctx: BindingContext,
    screenItems?: ScreenItemsContext,
  ): BindingResult {
    const unboundOld = this.unbind(oldVariable, ctx);
    const boundNew = this.bind(newVariable, ctx, screenItems);

    return { updatedVariables: [unboundOld, boundNew] };
  }

  // ────────────────────────────────────────────────────────────
  // Create helpers
  // ────────────────────────────────────────────────────────────

  /**
   * Create a new variable and bind it to a screen in one step.
   *
   * @param params - Variable creation parameters
   * @param ctx - Screen + optional section context
   * @param screenItems - Optional items for auto-populating screenBindings
   * @param existingIds - Existing variable IDs for collision avoidance
   * @returns Bound variable ready to persist
   */
  createAndBind(
    params: CreateVariableParams,
    ctx: BindingContext,
    screenItems?: ScreenItemsContext,
    existingIds?: string[],
  ): FunnelVariable {
    const id = params.id || variableFactory.generateId(params.name, existingIds);
    const variable = variableFactory.create({ ...params, id });
    return this.bind(variable, ctx, screenItems);
  }

  /**
   * Create a global variable (not bound to any screen).
   * Visible in conditions everywhere.
   *
   * @param params - Variable creation parameters
   * @param existingIds - Existing variable IDs for collision avoidance
   * @returns Global variable ready to persist
   */
  createGlobal(
    params: CreateVariableParams,
    existingIds?: string[],
  ): FunnelVariable {
    const id = params.id || variableFactory.generateId(params.name, existingIds);
    const variable = variableFactory.create({ ...params, id });
    (variable as FunnelVariableFull).scope = 'global';
    return variable;
  }

  // ────────────────────────────────────────────────────────────
  // Screen binding helpers (extracted from V2VariableBindingSection)
  // ────────────────────────────────────────────────────────────

  /**
   * Create VariableOptions from screen items.
   * Each item's variableValue becomes the option label.
   */
  createOptionsFromItems(
    items: Array<{ variableValue: string }>,
  ): VariableOption[] {
    return items.map(item => ({
      id: crypto.randomUUID(),
      label: item.variableValue,
    }));
  }

  /**
   * Build positional screenBindings: option[i].id → item[i].variableValue.
   */
  buildPositionalBindings(
    options: VariableOption[],
    items: Array<{ variableValue: string }>,
  ): Record<string, string> {
    const bindings: Record<string, string> = {};
    const limit = Math.min(options.length, items.length);
    for (let i = 0; i < limit; i++) {
      const optId = options[i].id || crypto.randomUUID();
      bindings[optId] = items[i].variableValue;
    }
    return bindings;
  }

  /**
   * Rebuild screenBindings for a screen after options or items change.
   * Used when user applies new mapping or syncs options.
   */
  rebuildScreenBindings(
    variable: FunnelVariable,
    screenId: string,
    newBindings: Record<string, string>,
  ): FunnelVariable {
    const v = { ...variable } as FunnelVariableFull;
    v.screenBindings = {
      ...(v.screenBindings || {}),
      [screenId]: newBindings,
    };
    return v as FunnelVariable;
  }

  /**
   * Update screenScoreEffects for a screen.
   */
  updateScoreEffects(
    variable: FunnelVariable,
    screenId: string,
    effects: unknown[],
  ): FunnelVariable {
    const v = { ...variable } as FunnelVariableFull;
    const existing = v.screenScoreEffects || {};
    (existing as Record<string, unknown[]>)[screenId] = effects;
    v.screenScoreEffects = existing;
    return v as FunnelVariable;
  }
}

/** Singleton instance */
export const variableBindingService = new VariableBindingService();
